/* assets/src/viewer/core/camera.js
 * Camera prospettica + FOV adattivo (port asgComputeAdaptiveFov :73-98)
 * e framing della scena (port syncSceneBounds :648-687).
 */
import * as THREE from 'three';
import { getViewerSettings, clamp } from '../settings.js';

export const DEFAULT_CAMERA_VECTOR = new THREE.Vector3(6, 4.5, 8.5).normalize();

export function createCamera(container) {
  const settings = getViewerSettings();
  const bounds = container.getBoundingClientRect();
  const w = Math.max(bounds.width || container.clientWidth || 1, 1);
  const h = Math.max(bounds.height || container.clientHeight || 1, 1);
  const camera = new THREE.PerspectiveCamera(settings.fov, w / h, 0.1, 180);
  camera.position.set(6, 4.5, 8.5);
  return camera;
}

export function computeAdaptiveFov(config, w, d, modelType) {
  const manualFov = parseFloat(config && config.modelDefaultFov != null ? config.modelDefaultFov : '');
  if (Number.isFinite(manualFov) && manualFov > 0) return clamp(manualFov, 30, 90);

  const maxSpan = Math.max(w || 0, d || 0);
  const minSpan = Math.max(0.01, Math.min(w || maxSpan || 1, d || maxSpan || 1));
  const ratio = maxSpan / minSpan;
  let fov = 43;

  if (modelType === 'fabric') fov = 45;
  if (modelType === 'glass') fov = 40;
  if (modelType === 'awning_arm' || modelType === 'awning_fall') fov = 44;
  if (modelType === 'awning_arm_8000evo') fov = 40;

  if (maxSpan <= 2.0) fov += 6;
  else if (maxSpan <= 3.0) fov += 3;
  else if (maxSpan >= 5.5) fov -= 4;
  else if (maxSpan >= 4.5) fov -= 2;

  if (ratio > 1.55) fov += 2;
  else if (ratio < 1.05) fov -= 1;

  return clamp(fov, 34, 56);
}

function viewVectorFor(modelType) {
  if (modelType === 'awning_arm_8000evo') return new THREE.Vector3(3.9, 1.45, 2.55).normalize();
  if (modelType === 'awning_arm' || modelType === 'awning_fall') return new THREE.Vector3(2.1, 0.88, 4.2).normalize();
  return DEFAULT_CAMERA_VECTOR.clone();
}

/**
 * Inquadra una bounding box. preserveView=true mantiene la posizione camera
 * (solo aggiorna near/far e i limiti di distanza), altrimenti riposiziona.
 * Ritorna la box clonata per chi tiene lo stato dei bounds correnti.
 */
export function fitBounds(camera, controls, box, { preserveView = false, modelType = null } = {}) {
  if (!camera || !controls || !box || box.isEmpty()) return null;

  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, size.z);

  camera.near = Math.max(0.1, maxSize / 150);
  camera.far = Math.max(120, maxSize * 20);
  camera.updateProjectionMatrix();

  if (preserveView) {
    controls.minDistance = Math.max(2, maxSize * 0.24);
    controls.maxDistance = Math.max(20, maxSize * 4.2);
    controls.update();
    return box.clone();
  }

  const fitHeightDistance = maxSize / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)));
  const fitWidthDistance = fitHeightDistance / Math.max(camera.aspect, 0.8);
  const distance = Math.max(5, Math.max(fitHeightDistance, fitWidthDistance) * 1.06);
  const offset = viewVectorFor(modelType).multiplyScalar(distance);
  const lift = size.y * (modelType === 'awning_arm_8000evo' ? 0.04 : 0.16);
  const liftedTarget = center.clone().add(new THREE.Vector3(0, lift, 0));

  camera.position.copy(liftedTarget.clone().add(offset));
  controls.target.copy(liftedTarget);
  controls.minDistance = Math.max(2, distance * 0.36);
  controls.maxDistance = Math.max(20, distance * 3.4);
  controls.update();
  if (controls.saveState) controls.saveState();
  return box.clone();
}
